import { FieldPath, SubmitHandler } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { Button } from '@givebutter/primitives/button';
import { useToast } from '@givebutter/primitives/toast';
import { faMerge } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { fetchTagById, mergeTags } from 'api/tags';
import { UseFormContactTags } from 'assets/js/pages/contacts/UseFormContactTags';
import * as Dialog from 'components/dialog';
import { useDialogRoute } from 'components/dialog-route/DialogRouteContext';
import * as Form from 'components/form';
import { SelectItemWithLabel } from 'components/select/types';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { conditionallyFetchOrNull } from 'helpers/reactQuery';
import { accountQueryKeys } from 'queries/account-query-keys';
import { GivebutterAxiosError } from 'types/error';

interface ContactTagMergeFormValues {
    name: string;
    tags: SelectItemWithLabel[];
}

const ContactTagMergeFormModal = () => {
    const toast = useToast();
    const { goBack } = useDialogRoute();
    const queryClient = useQueryClient();
    const { accountId } = useCurrentAccount();
    const { tag_id: tagId } = useParams<{ tag_id: string }>();

    const { data: tag } = useSuspenseQuery({
        queryKey: accountQueryKeys.tagById(accountId, +tagId!),
        queryFn: () => conditionallyFetchOrNull(() => fetchTagById(accountId, +tagId!), !!tagId),
    });

    const methods = Form.useGivebutterForm<ContactTagMergeFormValues>({
        defaultValues: {
            name: '',
            tags: tag ? [{ id: tag.id.toString(), label: tag.name }] : [],
        },
    });
    const { handleSubmit, path, setError, control } = methods;

    const { mutate: submit, isPending: isLoading } = useMutation({
        mutationFn: async (formValues: ContactTagMergeFormValues) => {
            const { name, tags } = formValues;

            return mergeTags(accountId, {
                name,
                tag_ids: tags.map(tag => Number(tag.id)),
                type: tag?.type!,
            });
        },
        onSuccess: () => {
            toast.success('Tags merged successfully');
            queryClient.removeQueries({ queryKey: accountQueryKeys.tagById(accountId, +tagId!) });
            queryClient.invalidateQueries({ queryKey: accountQueryKeys.current(accountId) });
            goBack();
        },
        onError: (error: GivebutterAxiosError) => {
            const errors = error.response?.data.errors;

            if (errors) {
                for (let i in errors) {
                    setError(i as FieldPath<ContactTagMergeFormValues>, {
                        message: errors[i][0],
                    });
                }
            } else {
                toast.error('Unable to merge tags');
            }
        },
    });

    return (
        <Form.Provider {...methods}>
            <Dialog.Body className="flex flex-col gap-4">
                <Dialog.Close />
                <FontAwesomeIcon
                    icon={faMerge}
                    className="flex h-5 w-5 rounded-full bg-gray-200 p-3"
                />
                <Dialog.Title className="text-left">Merge tags</Dialog.Title>
                <Dialog.Description>Combine multiple tags into a single new tag</Dialog.Description>
                <form
                    onSubmit={handleSubmit(submit as SubmitHandler<ContactTagMergeFormValues>)}
                    className="flex flex-col gap-8"
                >
                    <div className={'flex flex-col gap-y-6'}>
                        <Form.Container>
                            <Form.Label name={path('tags')}>
                                Tags to merge
                                <Form.LabelAsterisk />
                            </Form.Label>
                            <UseFormContactTags
                                name={'tags'}
                                control={control}
                                allowNewTags={false}
                                rules={{
                                    validate: (value?: SelectItemWithLabel[]) => {
                                        if (!value || value.length < 2) {
                                            return 'Please select at least two tags';
                                        }

                                        return true;
                                    },
                                }}
                            />
                            <Form.Error name={path('tags')} />
                        </Form.Container>
                        <Form.Container>
                            <Form.Label name={path('name')}>
                                New tag name
                                <Form.LabelAsterisk />
                            </Form.Label>
                            <Form.TextInputRoot
                                name={'name'}
                                control={control}
                                rules={{
                                    required: 'Please enter a tag name',
                                }}
                            >
                                <Form.TextInputForm placeholder="New tag name" />
                            </Form.TextInputRoot>
                            <Form.Error name={path('name')} />
                        </Form.Container>
                    </div>
                    <div className="flex justify-end gap-2">
                        <Dialog.Cancel asChild>
                            <Button variant="outline" color="neutral">
                                Cancel
                            </Button>
                        </Dialog.Cancel>
                        <Form.SubmitButton disabled={isLoading} isSubmitting={isLoading}>
                            Apply
                        </Form.SubmitButton>
                    </div>
                </form>
            </Dialog.Body>
        </Form.Provider>
    );
};

const Root = ContactTagMergeFormModal;

export { ContactTagMergeFormModal, Root };
